import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import { apiFetch } from '../../../../lib/api';
import PrintModal from '../../../../components/PrintModal';
import { showToast } from '../../../../components/Toast';
import { formatQuantity, formatCurrency } from '../../../../utils/formatters';
import { usePermesso } from '../../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../../hooks/useBloccoRecordContesto';
import { useDirtyForm } from '../../../../hooks/useDirtyForm';
import { API_DOCS } from './SchedaSal.api';
import './SchedaSal.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoSal = 'BOZZA' | 'EMESSO' | 'APPROVATO_CLIENTE' | 'APPROVATO_BACKOFFICE' | 'FATTURATO';
type TipoApprovazione = 'CLIENTE' | 'BACKOFFICE';

interface RigaSalServer {
  id: number;
  ordinePos: number;
  rigaOrdineId: number | null;
  rigaDistintaExtraId: number | null;
  descrizione: string;
  quantitaTotale: number | string | null;
  quantitaPrecedente: number | string;
  quantitaCorrente: number | string;
  prezzoUnitario: number | string;
  importoCorrente: number | string;
  rigaOrdine: {
    ordinePos: number;
    descrizione: string;
    quantita: number | string;
    ordine?: { id: number; numero: string } | null;
  } | null;
  rigaDistintaExtra: { id: number; distintaId: number; descrizione: string } | null;
}

interface StornoAccontoServer {
  id: number;
  proformaAccontoId: number;
  importoStornato: number | string;
  proforma: { id: number; numero: string; totale: number | string; dataPagamento: string | null };
}

interface LogEntry {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface SalDettaglio {
  id: number;
  societaId: number;
  sedeId: number | null;
  commessaId: number | null;
  ordineId: number | null;
  numero: string | null;
  stato: StatoSal;
  dataEmissione: string | null;
  dataApprovazione: string | null;
  tipoApprovazione: TipoApprovazione | null;
  riferimentoApprovazione?: string | null;
  noteApprovazione?: string | null;
  importoTotale: number | string | null;
  tipoDocumento: { id: number; codice: string; nome: string } | null;
  fatturaCollegata: { id: number; numero: string | null; stato: string } | null;
  createdAt: string;
  ordine: { id: number; numero: string; flussoCommerciale: string } | null;
  commessa: {
    id: number;
    numero: string;
    descrizione: string | null;
    clienteRagioneSociale: string | null;
  } | null;
  createdBy: { id: number; nome: string; cognome?: string | null } | null;
  righe: RigaSalServer[];
  storniAcconto: StornoAccontoServer[];
  logs: LogEntry[];
}

// Riga in editing locale (quantità/prezzo come stringhe con virgola)
interface RigaLocale {
  key: string;
  rigaOrdineId: number | null;
  rigaDistintaExtraId: number | null;
  descrizione: string;
  quantitaTotale: number | null;
  quantitaPrecedente: number;
  quantitaCorrente: string;
  prezzoUnitario: string;
  rigaOrdinePos: number | null;
  ordineNumero: string | null;
}

// Righe della sorgente SAL della commessa (GET /api/sal/sorgente), per "Evadi a saldo"
interface SorgenteRigaSaldo {
  rigaOrdineId: number;
  ordinePos: number;
  descrizione: string;
  quantitaTotale: number | null;
  quantitaPrecedente: number;
  residua: number | null;
  prezzoUnitario: number;
}

interface SorgenteOrdineSaldo {
  id: number;
  numero: string;
  righe: SorgenteRigaSaldo[];
}

// Storno acconto in editing locale (importo come stringa con virgola)
interface StornoLocale {
  key: string;
  proformaAccontoId: number;
  numero: string;
  totale: number;
  dataPagamento: string | null;
  importo: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_SAL: Record<StatoSal, { label: string; cls: string }> = {
  BOZZA:                { label: 'Bozza',                cls: 'bop-badge-neutral' },
  EMESSO:               { label: 'Emesso',               cls: 'bop-badge-info' },
  APPROVATO_CLIENTE:    { label: 'Approvato cliente',    cls: 'bop-badge-success' },
  APPROVATO_BACKOFFICE: { label: 'Approvato backoffice', cls: 'bop-badge-success' },
  FATTURATO:            { label: 'Fatturato',            cls: 'bop-badge-success' },
};

const TIPO_APPROVAZIONE_LABEL: Record<TipoApprovazione, string> = {
  CLIENTE: 'Cliente',
  BACKOFFICE: 'Backoffice',
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

function formatDateTimeIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('it-IT')} ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
}

function nomeUtente(u: { nome: string; cognome?: string | null } | null): string | null {
  if (!u) return null;
  return `${u.nome || ''} ${u.cognome || ''}`.trim() || null;
}

// Converte una stringa con virgola decimale in number
function toNum(s: string): number {
  return Number(s.replace(',', '.'));
}

// Da number/decimal server a stringa input con virgola
function numToInput(n: number | string | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '';
  return String(Number(n)).replace('.', ',');
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

// Importo live di una riga (null se quantità o prezzo non validi)
function importoRiga(r: RigaLocale): number | null {
  const q = toNum(r.quantitaCorrente);
  const p = toNum(r.prezzoUnitario);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return null;
  return Math.round(q * p * 100) / 100;
}

// Riga aggiunta a mano (integrazione / lavorazione extra): né riga ordine né voce di distinta
function isRigaLibera(r: RigaLocale): boolean {
  return r.rigaOrdineId === null && r.rigaDistintaExtraId === null;
}

let contatoreRigaNuova = 0;

function rigaServerToLocale(r: RigaSalServer): RigaLocale {
  return {
    key: `srv-${r.id}`,
    rigaOrdineId: r.rigaOrdineId,
    rigaDistintaExtraId: r.rigaDistintaExtraId,
    descrizione: r.descrizione,
    quantitaTotale: r.quantitaTotale !== null && r.quantitaTotale !== undefined ? Number(r.quantitaTotale) : null,
    quantitaPrecedente: Number(r.quantitaPrecedente ?? 0),
    quantitaCorrente: numToInput(r.quantitaCorrente),
    prezzoUnitario: numToInput(r.prezzoUnitario),
    rigaOrdinePos: r.rigaOrdine?.ordinePos ?? null,
    ordineNumero: r.rigaOrdine?.ordine?.numero ?? null,
  };
}

function stornoServerToLocale(s: StornoAccontoServer): StornoLocale {
  return {
    key: `srv-${s.id}`,
    proformaAccontoId: s.proformaAccontoId,
    numero: s.proforma?.numero ?? `#${s.proformaAccontoId}`,
    totale: Number(s.proforma?.totale ?? 0),
    dataPagamento: s.proforma?.dataPagamento ?? null,
    importo: numToInput(s.importoStornato),
  };
}

// Campo label/valore in sola lettura
function Campo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="sal-scheda__campo">
      <span className="sal-scheda__campo-label">{label}</span>
      <span className="sal-scheda__campo-value">
        {value === null || value === undefined || value === '' ? (
          <span className="bop-text-muted">—</span>
        ) : (
          value
        )}
      </span>
    </div>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function SchedaSal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermesso();

  const [sal, setSal] = useState<SalDettaglio | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  // Editing righe + storni acconto (solo BOZZA)
  const [righe, setRighe] = useState<RigaLocale[]>([]);
  // Righe correnti per i calcoli che finiscono dopo una chiamata di rete (Evadi a saldo)
  const righeRef = useRef<RigaLocale[]>([]);
  righeRef.current = righe;
  const [storni, setStorni] = useState<StornoLocale[]>([]);
  const [modificheSnapshot, setModificheSnapshot] = useState('');
  const [salvandoRighe, setSalvandoRighe] = useState(false);
  const [azioneInCorso, setAzioneInCorso] = useState(false);

  // Modale approvazione
  const [modalApprovaAperta, setModalApprovaAperta] = useState(false);
  const [tipoApprovazione, setTipoApprovazione] = useState<TipoApprovazione>('CLIENTE');
  const [riferimentoApprovazione, setRiferimentoApprovazione] = useState('');
  const [noteApprovazione, setNoteApprovazione] = useState('');
  const [approvando, setApprovando] = useState(false);

  // Stampa / invio al cliente: motore di stampa, template SAL di Impostazioni → Report
  const [modalStampaAperta, setModalStampaAperta] = useState(false);
  const [saldando, setSaldando] = useState(false);

  const etichetta = sal ? `SAL ${sal.numero ?? '(bozza)'}` : 'SAL';
  const isBozza = sal?.stato === 'BOZZA';
  const canEdit = can('commerciale.sal', 'edit');
  const canDelete = can('commerciale.sal', 'delete');
  const canFattureEdit = can('commerciale.fatture_attive', 'edit');
  const editabile = isBozza && canEdit;

  useBloccoRecordContesto({
    recordSocietaId: sal?.societaId,
    label: etichetta,
  });

  const modificheDirty =
    modificheSnapshot !== '' && JSON.stringify({ righe, storni }) !== modificheSnapshot;
  useDirtyForm(editabile && modificheDirty, etichetta);

  // ── Caricamento ─────────────────────────────────────────────────────────────

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      // @api GET /api/sal/:id -> {"success":true,"data":{"id":"number","societaId":"number","sedeId":"number","commessaId":"number","ordineId":"number|null","numero":"string|null","stato":"BOZZA|EMESSO|APPROVATO_CLIENTE|APPROVATO_BACKOFFICE|FATTURATO","dataEmissione":"string","dataApprovazione":"string","tipoApprovazione":"CLIENTE|BACKOFFICE","riferimentoApprovazione":"string","noteApprovazione":"string","importoTotale":"number","tipoDocumento":{"id":"number","codice":"string","nome":"string"},"fatturaCollegata":{"id":"number","numero":"string|null","stato":"string"},"createdAt":"string","ordine":{"id":"number","numero":"string","flussoCommerciale":"string"},"commessa":{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"},"createdBy":{"id":"number","nome":"string","cognome":"string"},"righe":[{"id":"number","ordinePos":"number","rigaOrdineId":"number|null","rigaDistintaExtraId":"number|null","descrizione":"string","quantitaTotale":"number|null","quantitaPrecedente":"number","quantitaCorrente":"number","prezzoUnitario":"number","importoCorrente":"number","rigaOrdine":{"ordinePos":"number","descrizione":"string","quantita":"number","ordine":{"id":"number","numero":"string"}},"rigaDistintaExtra":{"id":"number","distintaId":"number","descrizione":"string"}}],"storniAcconto":[{"id":"number","proformaAccontoId":"number","importoStornato":"number","proforma":{"id":"number","numero":"string","totale":"number","dataPagamento":"string|null"}}],"logs":[{"id":"number","utenteName":"string","azione":"string","dettaglio":"string","createdAt":"string"}]}}
      const r = await apiFetch<{ success: boolean; data?: SalDettaglio; error?: unknown }>(`/api/sal/${id}`);
      if (!r.success || !r.data) {
        setErrore(estraiErrore({ payload: r }, 'SAL non trovato'));
        setSal(null);
        return;
      }
      const d = r.data;
      setSal(d);
      const righeLocali = (d.righe || []).map(rigaServerToLocale);
      const storniLocali = (d.storniAcconto || []).map(stornoServerToLocale);
      setRighe(righeLocali);
      setStorni(storniLocali);
      setModificheSnapshot(JSON.stringify({ righe: righeLocali, storni: storniLocali }));
    } catch (err: unknown) {
      setErrore(estraiErrore(err, 'Errore nel caricamento del SAL'));
      setSal(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  // ── Azioni header ───────────────────────────────────────────────────────────

  const emetti = async () => {
    if (!sal) return;
    if (modificheDirty) {
      showToast('Salva le modifiche prima di emettere il SAL', 'error');
      return;
    }
    if (!window.confirm('Emettere il SAL? Verrà assegnato il numero dal numeratore SAL della sede e non sarà più modificabile.')) return;
    setAzioneInCorso(true);
    try {
      // @api POST /api/sal/:id/emetti -> {"success":true,"data":{"stato":"EMESSO","numero":"string","dataEmissione":"string"}}
      const r = await apiFetch<{ success: boolean; data?: { stato: string; numero: string; dataEmissione: string }; error?: unknown }>(
        `/api/sal/${sal.id}/emetti`,
        { method: 'POST' }
      );
      if (!r.success || !r.data) {
        showToast(estraiErrore({ payload: r }, "Errore nell'emissione del SAL"), 'error');
        return;
      }
      showToast(`SAL ${r.data.numero} emesso`, 'success');
      await carica();
    } catch (err: unknown) {
      // Es. numeratore SAL non configurato per la sede: mostrare il messaggio del backend
      showToast(estraiErrore(err, "Errore nell'emissione del SAL"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const elimina = async () => {
    if (!sal) return;
    if (!window.confirm(`Eliminare definitivamente il ${etichetta}?`)) return;
    setAzioneInCorso(true);
    try {
      // @api DELETE /api/sal/:id -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/sal/${sal.id}`, { method: 'DELETE' });
      showToast('SAL eliminato', 'success');
      navigate('/commerciale/attivo/sal');
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'eliminazione del SAL"), 'error');
      setAzioneInCorso(false);
    }
  };

  const apriModalApprova = () => {
    setTipoApprovazione('CLIENTE');
    setRiferimentoApprovazione('');
    setNoteApprovazione('');
    setModalApprovaAperta(true);
  };

  const approva = async () => {
    if (!sal) return;
    setApprovando(true);
    try {
      // @api POST /api/sal/:id/approva {"tipoApprovazione":"CLIENTE|BACKOFFICE","noteApprovazione":"string|null","riferimentoApprovazione":"string|null"} -> {"success":true,"data":{"stato":"APPROVATO_CLIENTE|APPROVATO_BACKOFFICE"}}
      const r = await apiFetch<{ success: boolean; data?: { stato: string }; error?: unknown }>(
        `/api/sal/${sal.id}/approva`,
        {
          method: 'POST',
          body: JSON.stringify({
            tipoApprovazione,
            noteApprovazione: noteApprovazione.trim() || null,
            riferimentoApprovazione: riferimentoApprovazione.trim() || null,
          }),
        }
      );
      if (!r.success) {
        showToast(estraiErrore({ payload: r }, "Errore nell'approvazione del SAL"), 'error');
        return;
      }
      showToast(`${etichetta} approvato`, 'success');
      setModalApprovaAperta(false);
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'approvazione del SAL"), 'error');
    } finally {
      setApprovando(false);
    }
  };

  const generaFattura = async () => {
    if (!sal) return;
    if (!window.confirm('Generare la fattura da questo SAL? Verrà creata una fattura immediata in bozza.')) return;
    setAzioneInCorso(true);
    try {
      // @api POST /api/fatture/da-sal {"salId":"number"} -> {"success":true,"data":{"id":"number"}}
      const r = await apiFetch<{ success: boolean; data?: { id: number }; error?: unknown }>(
        '/api/fatture/da-sal',
        { method: 'POST', body: JSON.stringify({ salId: sal.id }) }
      );
      if (!r.success || !r.data) {
        showToast(estraiErrore({ payload: r }, 'Errore nella generazione della fattura'), 'error');
        return;
      }
      showToast('Fattura generata in bozza', 'success');
      navigate(`/commerciale/attivo/fatture/${r.data.id}`);
    } catch (err: unknown) {
      // 409 SAL_NON_APPROVATO | GIA_FATTURATO: message dettagliato dal backend
      showToast(estraiErrore(err, 'Errore nella generazione della fattura'), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const apriStampa = () => {
    if (modificheDirty) {
      showToast('Salva le modifiche prima di stampare o inviare il SAL', 'error');
      return;
    }
    setModalStampaAperta(true);
  };

  // ── Editing righe ───────────────────────────────────────────────────────────

  const setCampoRiga = (key: string, campo: 'descrizione' | 'quantitaCorrente' | 'prezzoUnitario', val: string) => {
    setRighe(prev => prev.map(r => (r.key === key ? { ...r, [campo]: val } : r)));
  };

  // Aggiungi riga: integrazione o lavorazione extra fuori dagli ordini
  const aggiungiRiga = () => {
    contatoreRigaNuova += 1;
    setRighe(prev => [...prev, {
      key: `new-${contatoreRigaNuova}`,
      rigaOrdineId: null,
      rigaDistintaExtraId: null,
      descrizione: '',
      quantitaTotale: null,
      quantitaPrecedente: 0,
      quantitaCorrente: '1',
      prezzoUnitario: '',
      rigaOrdinePos: null,
      ordineNumero: null,
    }]);
  };

  // Evadi a saldo: porta ogni riga d'ordine alla quantità residua attuale e
  // aggiunge le righe con residuo non ancora nel SAL. Ambito: gli ordini già
  // presenti nel SAL; se il SAL non ha righe d'ordine, tutti gli ordini della
  // commessa. Le modifiche restano locali finché non si salva.
  const evadiASaldo = async () => {
    if (!sal?.commessaId) return;
    setSaldando(true);
    try {
      // @api GET /api/sal/sorgente?commessaId -> {"success":true,"data":{"ordini":[{"id":"number","numero":"string","righe":[{"rigaOrdineId":"number","ordinePos":"number","descrizione":"string","quantitaTotale":"number|null","quantitaPrecedente":"number","residua":"number|null","prezzoUnitario":"number"}]}]}}
      const r = await apiFetch<{ success: boolean; data?: { ordini: SorgenteOrdineSaldo[] } }>(
        `/api/sal/sorgente?commessaId=${sal.commessaId}`
      );
      const ordiniSorgente = r.data?.ordini ?? [];
      const righeAttuali = righeRef.current;
      const presenti = new Set(righeAttuali.filter(x => x.rigaOrdineId !== null).map(x => x.rigaOrdineId as number));
      const ordiniNelSal = ordiniSorgente.filter(o => o.righe.some(x => presenti.has(x.rigaOrdineId)));
      const ambito = ordiniNelSal.length > 0 ? ordiniNelSal : ordiniSorgente;

      let aggiornate = 0;
      let aggiunte = 0;
      let senzaResiduo = 0;
      const perRiga = new Map<number, { riga: SorgenteRigaSaldo; ordineNumero: string }>();
      for (const o of ambito) for (const x of o.righe) perRiga.set(x.rigaOrdineId, { riga: x, ordineNumero: o.numero });

      const nuove: RigaLocale[] = righeAttuali.map(x => {
        if (x.rigaOrdineId === null) return x;
        const src = perRiga.get(x.rigaOrdineId);
        if (!src) return x;
        const residua = src.riga.residua;
        if (residua === null || residua <= 0) { senzaResiduo += 1; return x; }
        aggiornate += 1;
        return {
          ...x,
          quantitaTotale: src.riga.quantitaTotale,
          quantitaPrecedente: src.riga.quantitaPrecedente,
          quantitaCorrente: numToInput(residua),
        };
      });
      for (const [rigaOrdineId, { riga, ordineNumero }] of Array.from(perRiga.entries())) {
        if (presenti.has(rigaOrdineId) || riga.residua === null || riga.residua <= 0) continue;
        aggiunte += 1;
        nuove.push({
          key: `saldo-${rigaOrdineId}`,
          rigaOrdineId,
          rigaDistintaExtraId: null,
          descrizione: riga.descrizione,
          quantitaTotale: riga.quantitaTotale,
          quantitaPrecedente: riga.quantitaPrecedente,
          quantitaCorrente: numToInput(riga.residua),
          prezzoUnitario: numToInput(riga.prezzoUnitario),
          rigaOrdinePos: riga.ordinePos,
          ordineNumero,
        });
      }

      if (aggiornate === 0 && aggiunte === 0) {
        showToast(senzaResiduo > 0
          ? 'Le righe del SAL non hanno più quantità residua: già tutto evaso'
          : 'Nessuna riga con quantità residua da portare a saldo', 'info');
        return;
      }
      setRighe(nuove);
      showToast(
        `A saldo: ${aggiornate} ${aggiornate === 1 ? 'riga aggiornata' : 'righe aggiornate'}, ${aggiunte} ${aggiunte === 1 ? 'aggiunta' : 'aggiunte'}. Salva le modifiche per confermare.`,
        'success'
      );
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel calcolo delle quantità residue'), 'error');
    } finally {
      setSaldando(false);
    }
  };

  const rimuoviRiga = (key: string) => {
    setRighe(prev => prev.filter(r => r.key !== key));
  };

  // ── Editing storni acconto ──────────────────────────────────────────────────

  const setImportoStorno = (key: string, val: string) => {
    setStorni(prev => prev.map(s => (s.key === key ? { ...s, importo: val } : s)));
  };

  const rimuoviStorno = (key: string) => {
    setStorni(prev => prev.filter(s => s.key !== key));
  };

  const totaleLive = useMemo(() => {
    let tot = 0;
    for (const r of righe) {
      const imp = importoRiga(r);
      if (imp !== null) tot += imp;
    }
    return Math.round(tot * 100) / 100;
  }, [righe]);

  const totaleStorniLive = useMemo(() => {
    let tot = 0;
    for (const s of storni) {
      const imp = toNum(s.importo);
      if (Number.isFinite(imp)) tot += imp;
    }
    return Math.round(tot * 100) / 100;
  }, [storni]);

  const nettoLive = Math.round((totaleLive - totaleStorniLive) * 100) / 100;

  const salvaModifiche = async () => {
    if (!sal) return;
    if (righe.length === 0) {
      showToast('Il SAL deve avere almeno una riga', 'error');
      return;
    }
    const payload: Array<{
      rigaOrdineId?: number;
      rigaDistintaExtraId?: number;
      descrizione: string;
      quantitaTotale: number | null;
      quantitaPrecedente: number;
      quantitaCorrente: number;
      prezzoUnitario: number;
      importoCorrente: number;
    }> = [];
    for (const r of righe) {
      const descrizione = r.descrizione.trim();
      if (!descrizione) {
        showToast('Indica la descrizione di ogni riga aggiunta', 'error');
        return;
      }
      const q = toNum(r.quantitaCorrente);
      if (!Number.isFinite(q) || q <= 0) {
        showToast(`Quantità corrente non valida per la riga "${descrizione}"`, 'error');
        return;
      }
      const p = toNum(r.prezzoUnitario);
      if (r.prezzoUnitario.trim() === '' || !Number.isFinite(p) || p < 0) {
        showToast(`Prezzo unitario non valido per la riga "${descrizione}"`, 'error');
        return;
      }
      payload.push({
        ...(r.rigaOrdineId !== null ? { rigaOrdineId: r.rigaOrdineId } : {}),
        ...(r.rigaDistintaExtraId !== null ? { rigaDistintaExtraId: r.rigaDistintaExtraId } : {}),
        descrizione,
        // Riga aggiunta a mano: la quantità "totale" è quella corrente
        quantitaTotale: isRigaLibera(r) ? q : r.quantitaTotale,
        quantitaPrecedente: r.quantitaPrecedente,
        quantitaCorrente: q,
        prezzoUnitario: p,
        importoCorrente: Math.round(q * p * 100) / 100,
      });
    }
    // Storni acconto correnti (replace completo insieme alle righe)
    const payloadStorni: Array<{ proformaAccontoId: number; importoStornato: number }> = [];
    for (const st of storni) {
      const imp = toNum(st.importo);
      if (!Number.isFinite(imp) || imp <= 0) {
        showToast(`Importo stornato non valido per la proforma ${st.numero}`, 'error');
        return;
      }
      payloadStorni.push({ proformaAccontoId: st.proformaAccontoId, importoStornato: imp });
    }

    setSalvandoRighe(true);
    try {
      // @api PUT /api/sal/:id {"righe":[{"rigaOrdineId":"number?","rigaDistintaExtraId":"number?","descrizione":"string","quantitaTotale":"number|null","quantitaPrecedente":"number","quantitaCorrente":"number","prezzoUnitario":"number","importoCorrente":"number"}],"storni":[{"proformaAccontoId":"number","importoStornato":"number"}]} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/sal/${sal.id}`, {
        method: 'PUT',
        body: JSON.stringify({ righe: payload, storni: payloadStorni }),
      });
      showToast('Modifiche SAL salvate', 'success');
      await carica();
    } catch (err: unknown) {
      // 409 SOVRA_EVASIONE | STORNO_NON_VALIDO: message dettagliato dal backend
      showToast(estraiErrore(err, 'Errore nel salvataggio delle modifiche'), 'error');
    } finally {
      setSalvandoRighe(false);
    }
  };

  const renderOrigine = (r: RigaLocale): React.ReactNode => {
    if (isRigaLibera(r)) return 'Riga aggiunta';
    if (r.rigaDistintaExtraId !== null) return 'Extra distinta';
    if (r.rigaOrdineId !== null) {
      return r.rigaOrdinePos !== null ? `Riga ordine #${r.rigaOrdinePos}` : 'Riga ordine';
    }
    return <span className="bop-text-muted">—</span>;
  };

  const badgeStato = useMemo(() => {
    if (!sal) return null;
    const cfg = STATO_SAL[sal.stato] ?? { label: sal.stato, cls: 'bop-badge-neutral' };
    return <span className={`bop-badge ${cfg.cls}`}>{cfg.label}</span>;
  }, [sal]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="sal-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento SAL…</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (errore || !sal) {
    return (
      <Layout>
        <div className="sal-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">SAL non disponibile</h3>
              <p className="bop-empty-state__text">{errore || 'SAL non trovato.'}</p>
              <BopBackLink to="/commerciale/attivo/sal" label="Torna ai SAL" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const s = sal;
  const approvato = !!s.dataApprovazione;
  // SAL multi-ordine: nessun ordine di testata, ogni riga porta il proprio ordine
  const multiOrdine = !s.ordine;
  const mostraStorni = storni.length > 0 || isBozza;

  return (
    <Layout>
      <div className="sal-scheda">
        <BopBackLink to="/commerciale/attivo/sal" label="Torna ai SAL" />

        {/* Header */}
        <header className="sal-scheda__header">
          <div className="sal-scheda__header-row">
            <div className="sal-scheda__header-left">
              <h1 className="bop-page-title">{etichetta}</h1>
              <div className="sal-scheda__badges">{badgeStato}</div>
            </div>
            <div className="sal-scheda__header-actions">
              <button
                className="bop-btn bop-btn-secondary"
                onClick={apriStampa}
                disabled={azioneInCorso}
                title="Anteprima, PDF o invio email al cliente con il template SAL di Impostazioni → Report"
              >
                Stampa / Invia
              </button>
              {isBozza && canEdit && (
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={emetti}
                  disabled={azioneInCorso}
                >
                  Emetti SAL
                </button>
              )}
              {isBozza && canDelete && (
                <button
                  className="bop-btn bop-btn-danger"
                  onClick={elimina}
                  disabled={azioneInCorso}
                >
                  Elimina
                </button>
              )}
              {s.stato === 'EMESSO' && canEdit && (
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={apriModalApprova}
                  disabled={azioneInCorso}
                >
                  Approva…
                </button>
              )}
              {s.fatturaCollegata ? (
                <Link
                  className="bop-btn bop-btn-secondary"
                  to={`/commerciale/attivo/fatture/${s.fatturaCollegata.id}`}
                >
                  Fattura {s.fatturaCollegata.numero ?? 'bozza'}
                </Link>
              ) : (
                (s.stato === 'APPROVATO_CLIENTE' || s.stato === 'APPROVATO_BACKOFFICE') &&
                canFattureEdit && (
                  <button
                    className="bop-btn bop-btn-primary"
                    onClick={generaFattura}
                    disabled={azioneInCorso}
                  >
                    Genera fattura
                  </button>
                )
              )}
            </div>
          </div>
        </header>

        {/* Informazioni */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Informazioni</h2>
          </div>
          <div className="bop-card__body">
            <div className="sal-scheda__grid">
              <Campo
                label="Ordine"
                value={
                  s.ordine ? (
                    <Link to={`/commerciale/attivo/ordini/${s.ordine.id}`}>{s.ordine.numero}</Link>
                  ) : (
                    <span className="bop-badge bop-badge-neutral">Più ordini</span>
                  )
                }
              />
              <Campo
                label="Commessa"
                value={
                  s.commessa ? (
                    <Link to={`/commerciale/commesse/${s.commessa.id}`}>
                      {s.commessa.numero}
                      {s.commessa.descrizione ? ` — ${s.commessa.descrizione}` : ''}
                    </Link>
                  ) : null
                }
              />
              <Campo label="Cliente" value={s.commessa?.clienteRagioneSociale || null} />
              <Campo label="Tipo documento" value={s.tipoDocumento?.nome || null} />
              <Campo label="Importo totale" value={formatCurrency(Number(s.importoTotale ?? 0))} />
              <Campo
                label="Creato da / il"
                value={`${nomeUtente(s.createdBy) || '—'} · ${formatDateTimeIT(s.createdAt)}`}
              />
              <Campo label="Emesso il" value={s.dataEmissione ? formatDateIT(s.dataEmissione) : null} />
              <Campo
                label="Approvato il"
                value={
                  approvato
                    ? `${formatDateIT(s.dataApprovazione)}${s.tipoApprovazione ? ` · ${TIPO_APPROVAZIONE_LABEL[s.tipoApprovazione]}` : ''}`
                    : null
                }
              />
              <Campo label="Riferimento approvazione" value={s.riferimentoApprovazione || null} />
              <Campo label="Note approvazione" value={s.noteApprovazione || null} />
            </div>
          </div>
        </div>

        {/* Righe */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Righe</h2>
            {editabile && (
              <div className="bop-sal-righe-actions">
                <button
                  className="bop-btn bop-btn-secondary bop-btn-sm"
                  onClick={evadiASaldo}
                  disabled={saldando || salvandoRighe || !s.commessaId}
                  title="Porta le righe d'ordine all'intera quantità residua e aggiunge quelle con residuo non ancora nel SAL"
                >
                  {saldando ? 'Calcolo…' : 'Evadi a saldo'}
                </button>
                <button
                  className="bop-btn bop-btn-secondary bop-btn-sm"
                  onClick={aggiungiRiga}
                  disabled={salvandoRighe}
                  title="Integrazione o lavorazione extra non prevista dagli ordini"
                >
                  + Aggiungi riga
                </button>
                <button
                  className="bop-btn bop-btn-primary bop-btn-sm"
                  onClick={salvaModifiche}
                  disabled={salvandoRighe || !modificheDirty}
                >
                  {salvandoRighe ? 'Salvataggio…' : 'Salva modifiche'}
                </button>
              </div>
            )}
          </div>
          <div className="bop-card__body">
            {righe.length === 0 ? (
              <div className="bop-empty-state">Nessuna riga nel SAL.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Descrizione</th>
                    <th>Origine</th>
                    {multiOrdine && <th>Ordine</th>}
                    <th className="bop-text-right">Q.tà totale</th>
                    <th className="bop-text-right">Q.tà precedente</th>
                    <th className="bop-text-right">Q.tà corrente</th>
                    <th className="bop-text-right">Prezzo unit.</th>
                    <th className="bop-text-right">Importo</th>
                    {editabile && <th className="bop-table__th--actions"></th>}
                  </tr>
                </thead>
                <tbody>
                  {righe.map((r, i) => {
                    const isExtra = r.rigaDistintaExtraId !== null;
                    const isLibera = isRigaLibera(r);
                    const imp = importoRiga(r);
                    return (
                      <tr key={r.key}>
                        <td>{i + 1}</td>
                        <td>
                          {editabile && isLibera ? (
                            <input
                              className="bop-input bop-sal-riga-libera__descrizione"
                              value={r.descrizione}
                              placeholder="Es. Integrazione impianto — lavorazione extra"
                              onChange={e => setCampoRiga(r.key, 'descrizione', e.target.value)}
                            />
                          ) : (
                            r.descrizione
                          )}
                        </td>
                        <td>{renderOrigine(r)}</td>
                        {multiOrdine && (
                          <td>{r.ordineNumero || <span className="bop-text-muted">—</span>}</td>
                        )}
                        <td className="bop-text-right">
                          {r.quantitaTotale !== null ? formatQuantity(r.quantitaTotale) : <span className="bop-text-muted">—</span>}
                        </td>
                        <td className="bop-text-right">{formatQuantity(r.quantitaPrecedente)}</td>
                        <td className="bop-text-right">
                          {editabile ? (
                            <input
                              className="bop-input sal-scheda__qta-input"
                              value={r.quantitaCorrente}
                              onChange={e => setCampoRiga(r.key, 'quantitaCorrente', e.target.value)}
                            />
                          ) : (
                            formatQuantity(toNum(r.quantitaCorrente))
                          )}
                        </td>
                        <td className="bop-text-right">
                          {editabile && (isExtra || isLibera) ? (
                            <input
                              className="bop-input sal-scheda__prezzo-input"
                              value={r.prezzoUnitario}
                              onChange={e => setCampoRiga(r.key, 'prezzoUnitario', e.target.value)}
                            />
                          ) : (
                            formatCurrency(toNum(r.prezzoUnitario))
                          )}
                        </td>
                        <td className="bop-text-right">
                          {imp !== null ? formatCurrency(imp) : <span className="bop-text-muted">—</span>}
                        </td>
                        {editabile && (
                          <td className="bop-table__td--actions">
                            <button
                              className="bop-btn bop-btn-danger bop-btn-sm"
                              onClick={() => rimuoviRiga(r.key)}
                            >
                              Rimuovi
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={multiOrdine ? 8 : 7} className="bop-text-right sal-scheda__tfoot-label">Totale righe</td>
                    <td className="bop-text-right sal-scheda__tfoot-value">{formatCurrency(totaleLive)}</td>
                    {editabile && <td />}
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>

        {/* Storno acconti */}
        {mostraStorni && (
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Storno acconti</h2>
            </div>
            <div className="bop-card__body">
              {storni.length === 0 ? (
                <div className="bop-empty-state">Nessuno storno acconti su questo SAL.</div>
              ) : (
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Proforma</th>
                      <th>Pagata il</th>
                      <th className="bop-text-right">Totale proforma</th>
                      <th className="bop-text-right">Importo stornato</th>
                      {editabile && <th className="bop-table__th--actions"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {storni.map(st => (
                      <tr key={st.key}>
                        <td>{st.numero}</td>
                        <td>{formatDateIT(st.dataPagamento)}</td>
                        <td className="bop-text-right">{formatCurrency(st.totale)}</td>
                        <td className="bop-text-right">
                          {editabile ? (
                            <input
                              className="bop-input sal-scheda__prezzo-input"
                              value={st.importo}
                              onChange={e => setImportoStorno(st.key, e.target.value)}
                            />
                          ) : (
                            formatCurrency(toNum(st.importo))
                          )}
                        </td>
                        {editabile && (
                          <td className="bop-table__td--actions">
                            <button
                              className="bop-btn bop-btn-danger bop-btn-sm"
                              onClick={() => rimuoviStorno(st.key)}
                            >
                              Rimuovi
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Riepilogo: Totale righe − Storni = Netto */}
              <div className="sal-scheda__riepilogo">
                <div className="sal-scheda__riepilogo-item">
                  <span className="sal-scheda__riepilogo-label">Totale righe</span>
                  <span className="sal-scheda__riepilogo-value">{formatCurrency(totaleLive)}</span>
                </div>
                <div className="sal-scheda__riepilogo-item">
                  <span className="sal-scheda__riepilogo-label">− Storni</span>
                  <span className="sal-scheda__riepilogo-value sal-scheda__riepilogo-value--storno">
                    {formatCurrency(totaleStorniLive)}
                  </span>
                </div>
                <div className="sal-scheda__riepilogo-item">
                  <span className="sal-scheda__riepilogo-label">= Netto</span>
                  <span className="sal-scheda__riepilogo-value sal-scheda__riepilogo-value--netto">
                    {formatCurrency(nettoLive)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Log attività */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Log attività</h2>
          </div>
          <div className="bop-card__body">
            {(s.logs || []).length === 0 ? (
              <div className="bop-empty-state">Nessuna voce di log.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data/ora</th>
                    <th>Utente</th>
                    <th>Azione</th>
                    <th>Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {s.logs.map(l => (
                    <tr key={l.id}>
                      <td>{formatDateTimeIT(l.createdAt)}</td>
                      <td>{l.utenteName}</td>
                      <td>{l.azione}</td>
                      <td>{l.dettaglio || <span className="bop-text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Modale stampa / invio (motore di stampa, modulo SAL) */}
        {modalStampaAperta && (
          <PrintModal
            societaId={s.societaId}
            entitaType="sal"
            entitaId={s.id}
            entitaLabel={etichetta}
            onClose={() => setModalStampaAperta(false)}
          />
        )}

        {/* Modale approvazione */}
        {modalApprovaAperta && (
          <div className="bop-modal-overlay" onClick={() => setModalApprovaAperta(false)}>
            <div className="bop-modal bop-modal--sm" onClick={e => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">Approva {etichetta}</h2>
                <button
                  type="button"
                  className="bop-modal__close"
                  onClick={() => setModalApprovaAperta(false)}
                  aria-label="Chiudi"
                >
                  ×
                </button>
              </div>
              <div className="bop-modal__body">
                <div className="bop-form-group">
                  <label className="bop-label">Tipo approvazione</label>
                  <select
                    className="bop-select"
                    value={tipoApprovazione}
                    onChange={e => setTipoApprovazione(e.target.value as TipoApprovazione)}
                  >
                    <option value="CLIENTE">Cliente</option>
                    <option value="BACKOFFICE">Backoffice</option>
                  </select>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Riferimento approvazione</label>
                  <input
                    className="bop-input"
                    value={riferimentoApprovazione}
                    onChange={e => setRiferimentoApprovazione(e.target.value)}
                    placeholder="Es. email del 01/03/2026"
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Note approvazione</label>
                  <textarea
                    className="bop-input"
                    rows={3}
                    value={noteApprovazione}
                    onChange={e => setNoteApprovazione(e.target.value)}
                  />
                </div>
              </div>
              <div className="bop-modal__footer">
                <button
                  type="button"
                  className="bop-btn bop-btn-secondary"
                  onClick={() => setModalApprovaAperta(false)}
                  disabled={approvando}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="bop-btn bop-btn-primary"
                  onClick={approva}
                  disabled={approvando}
                >
                  {approvando ? 'Approvazione…' : 'Conferma approvazione'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
